import React, { useEffect, useState } from 'react'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TablePagination from '@mui/material/TablePagination'
import Paper from '@mui/material/Paper'
import QRCode from 'qrcode.react'
import { useTranslation } from 'react-i18next'
import {
  calendar,
  deleteIcon,
  download,
  editIcon,
  eye,
  points,
  scan,
} from '../../images'
import { useNavigate } from 'react-router-dom'
import { formatDate } from '../../helpers/utils'

interface QRCodeValues {
  _id: string
  original_url: string
  short_url: string
  title: string
  domain: string
  custom_code: string
  has_qr: boolean
  status: number
  tracking_count: number
  createdAt: string
  qr_code: {
    color: string
    logo: string
  }
}

interface QrListProps {
  dataBody: QRCodeValues[]
  elevation?: number
  dataQrCodeSelected: (qrCode: QRCodeValues) => void
  setOpenDownload: (value: boolean) => void
  paginator?: boolean
  setOpenEdit: (value: boolean) => void
  setOpenDelete: (value: boolean) => void
  totalRecords: number
  totalPages: number
  onCounterChange: (value: number) => void
  onPageChange: (value: number) => void
}

const QrList = ({
  dataBody,
  totalRecords,
  totalPages,
  elevation = 1,
  dataQrCodeSelected,
  setOpenEdit,
  setOpenDelete,
  paginator = true,
  onCounterChange,
  onPageChange,
  setOpenDownload,
}: QrListProps) => {
  const { t } = useTranslation()
  const [showMenuExtra, setShowMenuExtra] = useState(false)
  const [cardIndex, setCardIndex] = useState(-1)
  let navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)
  const url = '/qr-codes/'

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
    onCounterChange(newPage + 1)
    onPageChange(newPage)
  }
  const handleMenuExtra = (index: number) => {
    setShowMenuExtra(!showMenuExtra)
    setCardIndex(index)
  }

  const handleDownload = (qrData: QRCodeValues) => {
    dataQrCodeSelected(qrData)
    setOpenDownload(true)
    setShowMenuExtra(false)
  }

  const handleEdit = (qrData: QRCodeValues) => {
    dataQrCodeSelected(qrData)
    setOpenEdit(true)
    setShowMenuExtra(false)
  }

  const handleDelete = (qrData: QRCodeValues) => {
    dataQrCodeSelected(qrData)
    setOpenDelete(true)
    setShowMenuExtra(false)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  const MenuExtra = (qrData: QRCodeValues) => {
    return (
      <div className='container-option-extra'>
        <button
          className='button-option-extra'
          onClick={() => handleDownload(qrData)}
        >
          <img
            src={download}
            alt={`${t('DOWNLOAD')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('DOWNLOAD')}`}</span>
        </button>
        <hr></hr>
        <button
          className='button-option-extra'
          onClick={() => handleEdit(qrData)}
        >
          <img
            src={editIcon}
            alt={`${t('EDIT')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('EDIT')}`}</span>
        </button>
        <hr></hr>
        <button
          className='button-option-extra'
          onClick={() => navigate(`${url}${qrData._id}`)}
        >
          <img
            src={eye}
            alt={`${t('VIEW_DETAIL')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('VIEW_DETAIL')}`}</span>
        </button>
        <hr></hr>
        <button
          className='button-option-extra'
          onClick={() => handleDelete(qrData)}
        >
          <img
            src={deleteIcon}
            alt={`${t('DELETE')}`}
            className='icon-option-extra'
          />
          <span className='option-extra'>{`${t('DELETE')}`}</span>
        </button>
      </div>
    )
  }

  return (
    <>
      <Paper elevation={elevation} className='table m-0 p-0'>
        <TableContainer className='table-container'>
          <Table stickyHeader aria-label='sticky table' id='table-to-xls'>
            <TableBody>
              {dataBody &&
                dataBody.map((value, index) => (
                  <>
                    <TableRow key={value._id}>
                      <TableCell>
                        <div className='container-qr-list'>
                          <QRCode
                            value={value.short_url}
                            fgColor={value.qr_code.color}
                            size={50}
                            bgColor='transparent'
                            level='H'
                            imageSettings={{
                              src: value.qr_code.logo,
                              height: 12,
                              width: 12,
                              excavate: true,
                            }}
                          />
                        </div>
                      </TableCell>
                      <TableCell
                        className='pointer'
                        onClick={() => navigate(`${url}${value._id}`)}
                      >
                        {value.title}
                      </TableCell>
                      <TableCell
                        className='pointer'
                        onClick={() => navigate(`${url}${value._id}`)}
                      >
                        {value.short_url}
                      </TableCell>
                      <TableCell>
                        <img
                          className='button-card-icon'
                          src={scan}
                          alt={`${t('SCAN')}`}
                        />
                        {value.tracking_count}
                      </TableCell>
                      <TableCell>
                        <img
                          className='button-card-icon'
                          src={calendar}
                          alt={`${t('DATE')}`}
                        />
                        {formatDate(value.createdAt)}
                      </TableCell>
                      <TableCell>
                        <button
                          className='button-general'
                          type='button'
                          onClick={() => handleMenuExtra(index)}
                        >
                          <img
                            className='button-card-icon'
                            src={points}
                            alt={`${t('SHOW_MENU')}`}
                          />
                        </button>
                        {showMenuExtra && cardIndex === index && (
                          <MenuExtra {...value} />
                        )}
                      </TableCell>
                    </TableRow>
                  </>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!!dataBody?.length && paginator && (
          <div className='paginator'>
            <TablePagination
              rowsPerPageOptions={[]}
              component='div'
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        )}
      </Paper>
    </>
  )
}

export default QrList
